import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';

const EPOCH = 1400000000;
const BASE62 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

function toBase62(bytes: Uint8Array): string {
    let num = 0n;
    for (const b of bytes) num = (num << 8n) | BigInt(b);
    let out = '';
    while (num > 0n) {
        out = BASE62[Number(num % 62n)] + out;
        num /= 62n;
    }
    return out.padStart(27, '0');
}

function genKsuid(): { id: string; ts: number } {
    const ts = Math.floor(Date.now() / 1000) - EPOCH;
    const buf = new Uint8Array(20);
    buf[0] = (ts >> 24) & 0xff;
    buf[1] = (ts >> 16) & 0xff;
    buf[2] = (ts >> 8) & 0xff;
    buf[3] = ts & 0xff;
    crypto.getRandomValues(buf.subarray(4));
    return { id: toBase62(buf), ts: ts + EPOCH };
}

interface State {
    count: number;
    output: string;
    copied: boolean;
}

export default class KsuidGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = { count: 5, output: '', copied: false };

    componentDidMount() {
        this.generate();
    }

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    generate = () => {
        const ids: string[] = [];
        for (let i = 0; i < this.state.count; i++) ids.push(genKsuid().id);
        this.setState({ output: ids.join('\n'), copied: false });
    };

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { count, output, copied } = this.state;
        const idCount = output ? output.split('\n').filter(Boolean).length : 0;

        const form = (
            <div className={genStyles.section}>
                <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Options</span></div>
                <div className={genStyles.field}>
                    <label htmlFor="ksuid-count">Number of IDs</label>
                    <input id="ksuid-count" type="number" inputMode="decimal" min={1} max={1000} value={count}
                        onChange={e => this.setState({ count: Math.max(1, Math.min(1000, +e.target.value || 5)) }, this.generate)} />
                </div>
                <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`} onClick={this.generate} style={{ alignSelf: 'flex-start' }}>
                    ↻ Regenerate
                </button>
                <p style={{ fontSize: '0.78rem', color: '#64748b', margin: 0 }}>
                    KSUID = 4-byte timestamp (since 2014-05-13) + 16-byte randomness, encoded as 27-char base62. Sort order matches generation time.
                </p>
            </div>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Generated KSUIDs"
                previewMeta={`${idCount} KSUID${idCount === 1 ? '' : 's'}`}
                preview={<pre className={genStyles.previewArea} aria-label="Generated KSUIDs">{output}</pre>}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : '📋 Copy all'}
                    </button>
                }
            />
        );
    }
}
